/**
 * Verzwaringsdeken-configurator.
 *
 * Draait op templates/product.configurator.liquid (sections/product-configurator.liquid).
 * De bezoeker kiest formaat → gewicht → hoes; elke combinatie is één échte deken of bundel
 * uit #configurator-data. Dit script zet de variant-ID van dat product in het ATC-formulier
 * (gewone POST naar /cart/add) en wisselt galerij, prijs, voorraad en specificaties via de
 * Section Rendering API (sections/configurator-product-info.liquid).
 *
 * De pagina start leeg (niets gekozen): de server rendert dan de inhoud van het overkoepelende
 * product (eigen foto's, 'vanaf'-prijs, algemene intro). Die beginstand staat als cache-item
 * `null` naast de opgehaalde producten; wordt de keuze weer onvolledig, dan komt hij terug langs
 * dezelfde weg als een echt product.
 *
 * Een sticky CTA-balk ([data-configurator-sticky]) verschijnt zodra de echte knop uit beeld is
 * gescrold en volgt de keuze: prijs, en een knop die toevoegt, naar de eerste open stap scrolt of
 * naar de voorraadmelding.
 *
 * Optioneel persoonlijk advies. Stap 1 vraagt eerst voor wie de deken is: volwassene (140x200 of
 * 240x220, allebei even goed, geen badge; de bezoeker kiest zelf) of kind (lengteslider wijst
 * 90x120, 100x150 of 140x200 aan, met een adviesregel eronder; passen er meerdere dan hangt er één
 * "Maak een keuze"-badge over die tegels). De gewichtsslider (met typbaar getal
 * ernaast) markeert de 1e/2e/3e keus gewicht (tabel #configurator-advice, gelijk aan de "Help mij
 * kiezen"-modal) en selecteert bij loslaten de 1e keus.
 *
 * Bij 240x220 kan optioneel het gewicht van een partner erbij (tweede slider); het gemiddelde van
 * beide bepaalt dan de keus-labels via de tweepersoonstabel.
 *
 * URL: ?size=140x200&weight=6&cover=...&for=adult&height=175&bodyweight=80&partnerweight=70
 * (replaceState, pas bij rust ná de laatste wijziging zodat de klik zelf snel paint en de volgende tik
 * niet op de gepatchte replaceState hoeft te wachten; bij het verlaten van de pagina wordt hij geflusht).
 * De keuze is leidend; height/bodyweight vullen de sliders en kiezen alleen zelf als er geen keuze
 * in de URL staat. Oude Nederlandse namen (formaat/gewicht/hoes) worden nog gelezen.
 */

import { fetchSectionDocument, initAccordeon } from './lib/section-render.js';

const SIZE_ORDER = ['90x120cm', '100x150cm', '140x200cm', '240x220cm'];
const NO_COVER = 'zonder-hoes';
const SLOTS = ['gallery', 'head', 'summary', 'stock', 'proof', 'after'];
// Reviewsectie onderaan (Judge.me All Reviews); de sterrenbadges scrollen erheen.
const REVIEWS_ANCHOR = '#judgeme_product_reviews';
// Keuze-params: eerste naam wordt geschreven, de rest alleen nog gelezen (en opgeruimd).
const PARAMS = { size: ['size', 'formaat'], weight: ['weight', 'gewicht'], cover: ['cover', 'hoes'] };
// Profiel-params per slider ([data-advice-input]).
const PROFILE_PARAMS = { length: 'height', weight: 'bodyweight', partner: 'partnerweight' };
// Voor wie de deken is (?for=adult|child): bepaalt de adviestabel en welke formaten stap 1 toont.
const AUDIENCE_PARAM = 'for';
const AUDIENCE_SIZES = {
  adult: ['140x200cm', '240x220cm'],
  child: ['90x120cm', '100x150cm', '140x200cm'],
};
// Bereik van de gewichtsliders (eigen gewicht en partner) per doelgroep; zonder doelgroep als volwassene.
const WEIGHT_BOUNDS = { adult: [35, 110], child: [18, 100] };
// Formaat waarbij het gewicht van een partner meetelt (advice.double, gemiddeld gewicht per persoon).
const DOUBLE_SIZE = '240x220cm';

const root = document.querySelector('[data-configurator]');
if (root) init(root);

/**
 * Leest de productdata, bepaalt de beginkeuze en koppelt de stappen aan het formulier.
 * @param {HTMLElement} root - het [data-configurator]-element
 */
function init(root) {
  const items = JSON.parse(document.getElementById('configurator-data').textContent);
  const i18n = JSON.parse(document.getElementById('configurator-i18n').textContent);
  const defaultId = Number(root.dataset.defaultId) || null;
  const sectionId = root.dataset.sectionId;
  const advice = JSON.parse(document.getElementById('configurator-advice').textContent);
  const startParams = new URLSearchParams(window.location.search);

  const steps = {
    size: root.querySelector('[data-step="size"]'),
    weight: root.querySelector('[data-step="weight"]'),
    cover: root.querySelector('[data-step="cover"]'),
  };
  const variantInput = root.querySelector('[data-configurator-variant]');
  const atc = root.querySelector('[data-configurator-atc]');
  const atcText = root.querySelector('[data-configurator-atc-text]');
  const notify = root.querySelector('[data-configurator-notify]');
  const sticky = root.querySelector('[data-configurator-sticky]');
  const stickyCta = sticky ? sticky.querySelector('[data-sticky-cta]') : null;
  // Het échte product waarvan de info nu in de slots staat (undefined zolang formaat of gewicht ontbreekt).
  let selected = defaultId ? items.find((i) => i.id === defaultId) : undefined;
  const adviceInputs = {
    length: root.querySelector('[data-advice-input="length"]'),
    weight: root.querySelector('[data-advice-input="weight"]'),
    partner: root.querySelector('[data-advice-input="partner"]'),
  };
  // Typbaar getal naast elke slider, met de eenheid ernaast ("+ kg" op de maximumstand).
  const adviceNumbers = {
    length: root.querySelector('[data-advice-number="length"]'),
    weight: root.querySelector('[data-advice-number="weight"]'),
    partner: root.querySelector('[data-advice-number="partner"]'),
  };
  const adviceUnits = {
    length: root.querySelector('[data-advice-unit="length"]'),
    weight: root.querySelector('[data-advice-unit="weight"]'),
    partner: root.querySelector('[data-advice-unit="partner"]'),
  };
  // Volwassene/kind-tegels in stap 1; de lengteslider ([data-length-row]) hoort alleen bij kind.
  const audienceButtons = [...root.querySelectorAll('[data-audience]')];
  const lengthRow = root.querySelector('[data-length-row]');
  // Adviesregel onder de lengteslider: het aanbevolen kindformaat, of "meerdere passen, kies zelf".
  const lengthAdvice = root.querySelector('[data-length-advice]');
  let audience = null;
  // Partnergewicht (alleen bij 240x220): knop toont de tweede slider.
  const partnerToggle = root.querySelector('[data-partner-toggle]');
  const partnerRow = root.querySelector('[data-partner-row]');
  let partnerOpen = false;

  // Opgehaalde productinfo per product-ID (Document per Section Rendering-antwoord). Sleutel null =
  // de server-gerenderde beginstand, zodat een onvolledige keuze langs dezelfde weg terugkeert.
  const cache = new Map();
  cache.set(
    null,
    new DOMParser().parseFromString(SLOTS.map((name) => (slot(name) ? slot(name).innerHTML : '')).join(''), 'text/html')
  );
  let requestToken = 0;
  // Staat van de uitgestelde URL-update (scheduleUrlSync); hier gedeclareerd omdat update() al tijdens init draait.
  let urlSyncPending = false;
  let urlSyncQueued = false;
  // Lengte/gewicht van de bezoeker (en van de partner); per waarde null zolang die slider niet is aangeraakt.
  const profile = { length: null, weight: null, partner: null };

  const state = initialState(items, defaultId, startParams);

  // Sterrenbadge (eigen badge, Judge.me-badge of in de sticky balk): naar de reviews scrollen. Capture,
  // zodat Judge.me's eigen handler (die een productwidget zoekt die hier niet staat) niet voorgaat.
  root.addEventListener(
    'click',
    (event) => {
      if (!event.target.closest('.jdgm-prev-badge')) return;
      event.preventDefault();
      event.stopPropagation();
      scrollToReviews();
    },
    true
  );
  root.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!event.target.matches('.jdgm-prev-badge[role="button"]')) return;
    event.preventDefault();
    scrollToReviews();
  });

  root.addEventListener('click', (event) => {
    const audienceButton = event.target.closest('[data-audience]');
    if (audienceButton) {
      if (audience === audienceButton.dataset.audience) return;
      audience = audienceButton.dataset.audience;
      // Een formaat dat niet bij deze keuze hoort (bv. 240x220 bij kind) vervalt.
      if (state.size && !AUDIENCE_SIZES[audience].includes(state.size)) state.size = null;
      applyAdvice();
      return;
    }
    const button = event.target.closest('[data-option]');
    if (!button) return;
    const step = button.closest('[data-step]').dataset.step;
    if (state[step] === button.dataset.option) return;
    state[step] = button.dataset.option;
    if (step === 'size' && !audience) audience = inferAudience(state.size);
    normalize(items, state);
    // Formaat gekozen terwijl het gewicht bekend is (bv. na een meerkeuze-advies): meteen de 1e keus.
    if (step === 'size' && !state.weight && profile.weight !== null) {
      const best = rankWithinSize(advice, profile, audience, state.size)[0];
      if (best) state.weight = best.weight;
      normalize(items, state);
    }
    update();
  });

  if (partnerToggle) {
    partnerToggle.addEventListener('click', () => {
      partnerOpen = true;
      renderPartner();
    });
  }

  Object.entries(adviceInputs).forEach(([key, input]) => {
    if (!input) return;
    paintRange(input);
    // Tijdens het schuiven alleen labels bijwerken; bij loslaten de 1e keus selecteren.
    input.addEventListener('input', () => {
      readProfile(key);
      renderAdviceSoon();
    });
    input.addEventListener('change', () => {
      readProfile(key);
      applyAdvice();
    });
  });

  Object.entries(adviceNumbers).forEach(([key, number]) => {
    const range = adviceInputs[key];
    if (!number || !range) return;
    // Tijdens het typen alleen meebewegen zodra het getal binnen het bereik valt ("1" van "175" niet).
    number.addEventListener('input', () => {
      const value = Number(number.value);
      if (number.value === '' || clampToInput(range, value) !== value) return;
      range.value = value;
      readProfile(key);
      renderAdviceSoon();
    });
    number.addEventListener('change', () => {
      if (number.value === '') {
        if (profile[key] !== null) number.value = range.value;
        return;
      }
      range.value = clampToInput(range, Number(number.value));
      number.value = range.value;
      readProfile(key);
      applyAdvice();
    });
    number.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') number.blur();
    });
  });

  // Omnisend zet bij de eerste poll na het laden zelf het overkoepelende product (uitverkocht) terug;
  // dan opnieuw onze keuze en status, anders begint het formulier elke seconde opnieuw te renderen.
  document.addEventListener('omnisend-viewed-product', (event) => {
    const detail = event.detail || {};
    const item = selected || null;
    const outOfStock = Boolean(item && item.notify);
    const wrongProduct = item && String(detail.productID) !== String(item.id);
    const wrongStatus = (detail.status === 'outOfStock') !== outOfStock;
    if (wrongProduct || wrongStatus) syncOmnisendProduct(item, outOfStock);
  });

  if (sticky) {
    if ('IntersectionObserver' in window) {
      // Alleen tonen als de echte knop bóven het beeld verdwenen is (de bezoeker scrolde er voorbij).
      new IntersectionObserver(([entry]) => {
        setStickyVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(atc);
    }
    stickyCta.addEventListener('click', () => {
      if (selected && selected.available) {
        atc.click();
        return;
      }
      let target = atc;
      if (!selected) target = state.size ? steps.weight : steps.size;
      else if (notify && !notify.hidden) target = notify;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  // FAQ: pas laden als de bezoeker in de buurt scrolt; daarna wisselen tussen kind/volwassen bij een andere keuze.
  const faq = document.querySelector('[data-configurator-faq]');
  const faqCache = new Map();
  let faqGroup = null;
  if (faq && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        loadFaq();
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(faq);
  } else if (faq) {
    loadFaq();
  }
  // De FAQ-sheet (src/faq-sheet.js) opent voordat de bezoeker bij de FAQ-sectie is gescrold.
  if (faq) document.addEventListener('faq-sheet:request', () => loadFaq());

  // ?for=&height=&bodyweight= zetten de keuze en de sliders; zonder formaat in de URL kiezen ze ook
  // meteen de 1e keus (of tonen "Maak een keuze").
  const choiceInUrl = PARAMS.size.some((name) => startParams.has(name));
  const forParam = startParams.get(AUDIENCE_PARAM);
  audience = Object.keys(AUDIENCE_SIZES).includes(forParam) ? forParam : inferAudience(state.size);
  // Eerst het bereik van de doelgroep, anders klemt een volwassen gewicht boven 100 kg op de kindgrens.
  applyWeightBounds();
  let profileInUrl = false;
  Object.entries(PROFILE_PARAMS).forEach(([key, name]) => {
    const input = adviceInputs[key];
    const value = Number(startParams.get(name));
    if (!input || !startParams.has(name) || !Number.isFinite(value)) return;
    input.value = clampToInput(input, value);
    readProfile(key);
    profileInUrl = true;
  });
  if ((profileInUrl || audience) && !choiceInUrl) applyAdvice();
  else update();

  /** Slot-element van de wisselbare productinfo (gallery, head, summary, stock, proof, after). */
  function slot(name) {
    return root.querySelector(`[data-configurator-slot="${name}"]`);
  }

  /** Container met de optietegels van een stap. */
  function optionsOf(step) {
    return step.querySelector('[data-step-options]');
  }

  /**
   * Haalt de FAQ op van een echte deken (sections/product-template.liquid via de Section Rendering
   * API): de kind-FAQ bij 90x120/100x150, anders die voor volwassenen, zoals snippets/product-faq.liquid
   * dat op productpagina's bepaalt. Zo blijft de FAQ op één plek beheerd in de theme-editor.
   */
  async function loadFaq() {
    const group = inferAudience(state.size) === 'child' ? 'kid' : 'adult';
    if (group === faqGroup) return;
    faqGroup = group;
    const url = group === 'kid' ? faq.dataset.faqKid : faq.dataset.faqAdult;
    if (!url) return;
    try {
      if (!faqCache.has(group)) {
        const doc = await fetchSectionDocument(url, 'product-template');
        // div.container: de <h2> in #tabpanel-faq heeft ook de class 'container'.
        const body = doc.querySelector('#tabpanel-faq div.container');
        faqCache.set(group, body ? body.innerHTML : '');
      }
      if (group !== faqGroup) return;
      const target = faq.querySelector('[data-faq-body]');
      target.innerHTML = faqCache.get(group);
      initAccordeon(target);
      document.dispatchEvent(new CustomEvent('faq:loaded'));
    } catch (error) {
      // Alleen terugzetten als er intussen geen nieuwere groep is aangevraagd, anders gooit die zijn antwoord weg.
      if (group === faqGroup) faqGroup = null;
    }
  }

  /**
   * Geeft Omnisend het product, de variant en de voorraadstatus van de gekozen deken. Het
   * voorraadmeldingsformulier leest productID/variantID pas bij het versturen, dus de aanmelding komt
   * op de échte variant in plaats van op het overkoepelende product.
   *
   * De status moet kloppen met wat wij tonen: Omnisend controleert elke seconde of het formulier
   * zichtbaar is en rendert het opnieuw (met een track-request) zolang de status 'outOfStock' is en de
   * container geen afmeting heeft. Een verborgen [data-configurator-notify] met 'outOfStock' geeft dus
   * een eindeloze stroom requests; daarom alleen 'outOfStock' als de voorraadmelding echt zichtbaar is.
   * @param {object|null} item - item uit #configurator-data, of null zonder complete keuze
   * @param {boolean} outOfStock - true als de voorraadmelding voor dit item getoond wordt
   */
  function syncOmnisendProduct(item, outOfStock) {
    const omnisend = window._omnisend;
    if (!omnisend || typeof omnisend.setProductInfo !== 'function') return;
    const status = outOfStock ? 'outOfStock' : 'inStock';
    let product;
    if (item) {
      const url = new URL(item.url, window.location.origin).href;
      const imageUrl = item.image2x ? new URL(item.image2x, window.location.href).href : '';
      product = {
        id: String(item.id),
        variantId: String(item.variantId),
        status,
        currency: i18n.currency,
        price: item.price,
        oldPrice: item.compareAt || item.price,
        title: item.title,
        description: '',
        imageUrl,
        url,
        productTitle: item.title,
        productImageUrl: imageUrl,
        productUrl: url,
      };
    } else {
      // Geen complete keuze: Omnisends eigen gegevens (het overkoepelende product) houden, alleen de
      // status corrigeren. Zonder gegevens is er niets te corrigeren; de listener hierboven pakt het
      // op zodra Omnisend zijn product zet.
      const current = typeof omnisend.getProductInfo === 'function' ? omnisend.getProductInfo() : null;
      if (!current || current.status === status) return;
      product = {
        id: current.productID,
        variantId: current.variantID,
        status,
        currency: current.currency,
        price: current.price,
        oldPrice: current.oldPrice,
        title: current.title,
        description: current.description,
        imageUrl: current.imageUrl,
        url: current.url,
        productTitle: current.productTitle,
        productImageUrl: current.productImageUrl,
        productUrl: current.productUrl,
      };
    }
    omnisend.setProductInfo({ properties: { product } });
  }

  /** Zet --fill (0..1) op een slider zodat de CSS het oranje deel van de track tot aan de knop vult. */
  function paintRange(input) {
    const min = Number(input.min);
    const max = Number(input.max);
    const fill = max > min ? (Number(input.value) - min) / (max - min) : 0;
    input.style.setProperty('--fill', String(fill));
  }

  /** Leest één slider uit en zet het getal ernaast gelijk; de maximumstand toont als "110+ kg". */
  function readProfile(key) {
    const input = adviceInputs[key];
    paintRange(input);
    profile[key] = Number(input.value);
    const number = adviceNumbers[key];
    // Niet overschrijven terwijl de bezoeker erin typt (cursor zou verspringen).
    if (number && document.activeElement !== number) number.value = input.value;
    const unit = adviceUnits[key];
    if (unit) unit.textContent = `${input.value === input.max ? '+' : ''} ${unit.dataset.unit}`;
  }

  // Tijdens het schuiven de tegels hoogstens één keer per frame opnieuw tekenen.
  let adviceRenderQueued = false;
  function renderAdviceSoon() {
    if (adviceRenderQueued) return;
    adviceRenderQueued = true;
    requestAnimationFrame(() => {
      adviceRenderQueued = false;
      renderSize();
      renderWeight();
    });
  }

  /**
   * Selecteert de 1e keus: past precies één formaat (kind met lengte), dan dat formaat; passen er
   * meerdere (volwassene: 140x200 én 240x220), dan blijft een al gekozen formaat uit die set staan en
   * wordt anders geen formaat gekozen: de bezoeker kiest dat zelf (zie renderSize). Met een formaat
   * én gewicht daarna het beste gewicht binnen dat formaat.
   */
  function applyAdvice() {
    // Vóór het rangschikken: een gewicht buiten het bereik van de (nieuwe) doelgroep wordt eerst geklemd.
    applyWeightBounds();
    const sizes = recommendedSizes(advice, profile, audience);
    if (sizes.length === 1) state.size = sizes[0];
    else if (sizes.length > 1 && !sizes.includes(state.size)) state.size = null;
    if (profile.weight !== null && state.size) {
      const best = rankWithinSize(advice, profile, audience, state.size)[0];
      if (best) state.weight = best.weight;
    }
    normalize(items, state);
    update();
  }

  /**
   * URL-parameters pas ná de paint bijwerken. history.replaceState is op deze pagina gepatcht door
   * trackingscripts (GTM/Clarity/pixels) en kost daardoor ~0,5 s per aanroep (4x CPU, ≈ 0,13 s echt).
   * In de click-handler telt dat mee voor INP; als losse taak later (debounce of idle-callback) vangt
   * hij juist de vólgende tik (gemeten: 1 s wachttijd bij een tik 0,4–1,2 s na de vorige). Direct na de
   * paint van de tegel-highlight is de veiligste plek: de highlight is al zichtbaar en een volgende tik
   * komt vrijwel nooit binnen ~0,3 s. Meerdere klikken vóór dat moment worden één schrijfactie.
   * Wie de pagina eerder verlaat (add-to-cart is een gewone POST), krijgt de URL alsnog via pagehide.
   */
  function scheduleUrlSync() {
    urlSyncPending = true;
    if (urlSyncQueued) return;
    urlSyncQueued = true;
    requestAnimationFrame(() => {
      setTimeout(() => {
        urlSyncQueued = false;
        flushUrlSync();
      }, 0);
    });
  }

  /** Schrijft een nog openstaande URL-update meteen; doet niets als er geen wijziging wacht. */
  function flushUrlSync() {
    if (!urlSyncPending) return;
    urlSyncPending = false;
    syncUrl();
  }
  window.addEventListener('pagehide', flushUrlSync);

  /** Schrijft de huidige keuze en het profiel als query-parameters (replaceState, geen history-entry). */
  function syncUrl() {
    const params = new URLSearchParams(window.location.search);
    Object.values(PARAMS).forEach(([, ...legacy]) => legacy.forEach((name) => params.delete(name)));
    [
      [PARAMS.size[0], withoutUnit(state.size, 'cm')],
      [PARAMS.weight[0], withoutUnit(state.weight, 'kg')],
      [PARAMS.cover[0], state.cover],
      [AUDIENCE_PARAM, audience],
      [PROFILE_PARAMS.length, profile.length],
      [PROFILE_PARAMS.weight, profile.weight],
      [PROFILE_PARAMS.partner, profile.partner],
    ].forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    const query = params.toString();
    history.replaceState(
      history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );
  }

  /** Rendert de stappen opnieuw en past formulier, URL en productinfo aan op de keuze. */
  function update() {
    const item = state.size && state.weight ? resolve(items, state) : undefined;
    const changed = (item ? item.id : null) !== (selected ? selected.id : null);
    selected = item;
    applyWeightBounds();
    renderAudience();
    renderSize();
    renderWeight();
    renderCover();
    renderPartner();

    const ready = Boolean(item);
    variantInput.value = ready ? item.variantId : '';
    atc.disabled = !ready || !item.available;
    atc.classList.toggle('disable', atc.disabled);
    atcText.textContent = ctaLabel(item);
    const showNotify = ready && Boolean(item.notify);
    if (notify) notify.hidden = !showNotify;
    // snippets/social-meta-tags.liquid rendert og:price:amount zonder content; alleen dit script vult hem.
    const ogPrice = document.querySelector('meta[property="og:price:amount"]');
    if (ogPrice) ogPrice.setAttribute('content', ((ready ? item.price : i18n.fromPrice) / 100).toFixed(2));
    syncOmnisendProduct(ready ? item : null, showNotify);

    scheduleUrlSync();

    if (changed) loadInfo(item || null);

    updateSticky();

    // FAQ al geladen? Dan meewisselen tussen kind- en volwassenenversie.
    if (faqGroup) loadFaq();

    // Publieke hook voor externe scripts (tracking); het thema zelf luistert er niet naar. De URL loopt
    // op dit moment nog achter (zie scheduleUrlSync): gebruik detail.item, niet location.
    root.dispatchEvent(new CustomEvent('configurator:change', { detail: { item } }));
  }

  /** Knoptekst bij de keuze: eerst kiezen, in winkelwagen, of niet leverbaar. */
  function ctaLabel(item) {
    if (!item) return i18n.chooseFirst;
    return item.available ? i18n.addToCart : i18n.unavailable;
  }

  /** Toont of verbergt de sticky balk; schuift de chat-bubbel mee omhoog zoals op productpagina's. */
  function setStickyVisible(show) {
    sticky.dataset.visible = String(show);
    sticky.setAttribute('aria-hidden', String(!show));
    stickyCta.tabIndex = show ? 0 : -1;
    const chat = document.getElementById('contact-widget');
    if (chat) chat.classList.toggle('chatbox-up', show);
    const footer = document.querySelector('footer');
    if (footer && footer.parentElement) footer.parentElement.style.paddingBottom = show ? `${sticky.offsetHeight}px` : '';
  }

  /** Zet knoptekst en prijs van de sticky balk gelijk aan de huidige keuze. */
  function updateSticky() {
    if (!sticky) return;
    const label = ctaLabel(selected);
    sticky.querySelector('[data-sticky-cta-text]').textContent = label;
    stickyCta.setAttribute('aria-label', label);
    // 'atc': op mobiel alleen het winkelwagenicoon tonen (zie de section).
    stickyCta.dataset.mode = !selected ? 'choose' : selected.available ? 'atc' : 'soldout';

    // Gekozen combinatie: afmeting · gewicht · hoes (alleen wat al gekozen is).
    const parts = [];
    if (state.size) parts.push(state.size.replace(/cm$/, ' cm'));
    if (state.weight) parts.push(state.weight.replace(/kg$/, ' kg'));
    if (selected) parts.push(coverLabel(selected));
    const choice = sticky.querySelector('[data-sticky-choice]');
    choice.textContent = parts.join(' · ');
    choice.hidden = !parts.length;
    updateStickyPrice();
  }

  /** Neemt de prijs over uit het kopblok (lege stand: 'vanaf'-prijs; anders prijs met doorgestreepte prijs). */
  function updateStickyPrice() {
    if (!sticky) return;
    const source = root.querySelector('[data-configurator-slot="head"] .pt-price');
    sticky.querySelector('[data-sticky-price]').innerHTML = source ? source.innerHTML : '';
  }

  /** Vertaalde hoesnaam van een item ('Zonder hoes' uit de data is Nederlands). */
  function coverLabel(item) {
    return item.coverKey === NO_COVER ? i18n.withoutCover : item.cover;
  }

  function scrollToReviews() {
    const target = document.querySelector(REVIEWS_ANCHOR);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Zet min/max van de gewichtsliders op de doelgroep (WEIGHT_BOUNDS) en klemt een al ingevulde
   * waarde binnen het nieuwe bereik. Idempotent: ongewijzigde grenzen doen niets.
   */
  function applyWeightBounds() {
    const [min, max] = WEIGHT_BOUNDS[audience === 'child' ? 'child' : 'adult'];
    ['weight', 'partner'].forEach((key) => {
      const range = adviceInputs[key];
      if (!range || (Number(range.min) === min && Number(range.max) === max)) return;
      const number = adviceNumbers[key];
      [range, number].forEach((input) => {
        if (!input) return;
        input.min = String(min);
        input.max = String(max);
      });
      range.value = clampToInput(range, Number(range.value));
      if (profile[key] !== null) readProfile(key);
      else paintRange(range);
    });
  }

  /** Markeert de gekozen doelgroep en toont de lengteslider alleen bij kind. */
  function renderAudience() {
    audienceButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.audience === audience)));
    if (lengthRow) lengthRow.hidden = audience !== 'child';
  }

  function renderSize() {
    // Zonder doelgroep alle formaten; daarna alleen die bij volwassene of kind horen.
    const sizes = (audience ? AUDIENCE_SIZES[audience] : SIZE_ORDER).filter((size) => items.some((i) => i.size === size));
    const recommended = recommendedSizes(advice, profile, audience);
    // Meerdere formaten even goed: bij een volwassene (140x200 én 240x220) gewoon geen badge, de
    // tegels spreken voor zich; bij een kind één "Maak een keuze"-badge over die tegels en zodra
    // er één van gekozen is "Gekozen" op die tegel. Eén passend formaat krijgt "Aanbevolen".
    const showGroup = recommended.length > 1 && audience === 'child';
    const chosen = showGroup && recommended.includes(state.size);
    renderOptions(steps.size, sizes, (button, size) => {
      button.querySelector('[data-label]').textContent = size;
      button.querySelector('[data-sublabel]').textContent = i18n.sizes[size] || '';
      markSoldOut(button, !items.some((i) => i.size === size && i.available));
      let badge = '';
      if (chosen && state.size === size) badge = i18n.chosen;
      else if (recommended.length === 1 && recommended[0] === size) badge = i18n.recommended;
      setBadge(button, badge, 1);
      return state.size === size;
    });
    // Baan (segmented control) precies zo breed als het aantal tegels.
    optionsOf(steps.size).style.setProperty('--cols', String(sizes.length));
    if (showGroup && !chosen) renderGroupBadge(sizes, recommended);
    renderLengthAdvice(recommended);
  }

  /** Adviesregel onder "Lengte kind": verborgen zonder advies, anders het formaat of "kies zelf". */
  function renderLengthAdvice(recommended) {
    if (!lengthAdvice) return;
    const show = audience === 'child' && recommended.length > 0;
    lengthAdvice.hidden = !show;
    if (!show) return;
    if (recommended.length === 1) {
      lengthAdvice.textContent = i18n.lengthAdviceOne.replace('__size__', recommended[0]);
    } else {
      lengthAdvice.textContent = i18n.lengthAdviceMany;
    }
  }

  /**
   * Partnerknop en -slider alleen bij 240x220; de slider zodra de knop is gebruikt of er al een
   * partnergewicht is (bv. uit ?partnerweight=).
   */
  function renderPartner() {
    if (!partnerToggle || !partnerRow) return;
    const double = state.size === DOUBLE_SIZE;
    const open = double && (partnerOpen || profile.partner !== null);
    partnerRow.hidden = !open;
    partnerToggle.hidden = !double || open;
  }

  /**
   * Hangt de gezamenlijke badge boven de aanbevolen tegels (altijd aaneengesloten in SIZE_ORDER):
   * een absoluut gepositioneerd grid-item over hun kolommen, met per tegel een pijltje.
   */
  function renderGroupBadge(sizes, recommended) {
    const template = steps.size.querySelector('[data-group-badge-template]');
    if (!template) return;
    const badge = template.content.firstElementChild.cloneNode(true);
    const first = sizes.indexOf(recommended[0]);
    badge.style.gridColumn = `${first + 1} / span ${recommended.length}`;
    const arrows = badge.querySelector('[data-group-arrows]');
    const arrow = arrows.querySelector('[data-group-arrow-template]');
    recommended.forEach(() => arrows.appendChild(arrow.content.firstElementChild.cloneNode(true)));
    optionsOf(steps.size).appendChild(badge);
  }

  function renderWeight() {
    // Stap 2 pas na een formaat, zoals stap 3 pas na een gewicht.
    steps.weight.hidden = !state.size;
    const blankets = uniqueBy(
      items
        .filter((i) => i.size === state.size && i.coverKey === NO_COVER)
        .sort((a, b) => parseFloat(a.weight) - parseFloat(b.weight)),
      'weight'
    );
    const ranking = profile.weight !== null ? rankWithinSize(advice, profile, audience, state.size) : [];
    optionsOf(steps.weight).style.setProperty('--cols', String(blankets.length));
    renderOptions(steps.weight, blankets, (button, blanket) => {
      button.dataset.option = blanket.weight;
      button.querySelector('[data-label]').textContent = blanket.weight.replace(/kg$/, ' kg');
      markSoldOut(button, !items.some((i) => i.size === state.size && i.weight === blanket.weight && i.available));
      const rank = ranking.findIndex((row) => row.weight === blanket.weight);
      setBadge(button, rank >= 0 ? i18n.choice[rank] : '', rank + 1);
      return state.weight === blanket.weight;
    });
  }

  function renderCover() {
    steps.cover.hidden = !state.weight;
    if (!state.weight) {
      optionsOf(steps.cover).replaceChildren();
      return;
    }
    const options = items.filter((i) => i.size === state.size && i.weight === state.weight);
    // 'Zonder hoes' eerst, daarna de bundels in collectievolgorde.
    options.sort((a, b) => (b.coverKey === NO_COVER) - (a.coverKey === NO_COVER));
    renderOptions(steps.cover, options, (button, option) => {
      const isBundle = option.coverKey !== NO_COVER;
      button.dataset.option = option.coverKey;
      button.title = isBundle ? option.cover : i18n.withoutCover;
      button.querySelector('[data-label]').innerHTML = isBundle ? option.coverHtml : i18n.withoutCoverHtml;
      const img = button.querySelector('[data-image]');
      if (option.image) {
        img.src = option.image;
        img.srcset = `${option.image} 160w, ${option.image2x} 320w`;
        img.alt = button.title;
      }
      if (isBundle) img.classList.add('object-[0_5px]', 'scale-[1.2]');
      markSoldOut(button, !option.available);
      return state.cover === option.coverKey;
    });
    steps.cover.querySelector('[data-step-value]').textContent = selected ? coverLabel(selected) : '';
  }

  /**
   * Markeert een optie als uitverkocht (blijft klikbaar voor de voorraadmelding). De formaat- en
   * gewichttegels stylen zichzelf via data-[sold-out=true]:; voor de hoes-swatches zet renderOptions
   * daarna de klassen uit [data-soldout-class] op de omliggende elementen.
   */
  function markSoldOut(button, soldOut) {
    if (!soldOut) return;
    button.dataset.soldOut = 'true';
    button.title = button.title ? `${button.title} - ${i18n.soldOut}` : i18n.soldOut;
  }

  /**
   * Zet de productinfo van de keuze in de wisselbare slots: uit de cache, of eerst opgehaald via de
   * Section Rendering API. Alleen het antwoord op de laatste aanvraag wordt getoond (snel doorklikken).
   * @param {object|null} item - item uit #configurator-data; null = de lege beginstand
   */
  async function loadInfo(item) {
    const token = ++requestToken;
    const key = item ? item.id : null;
    const slots = SLOTS.map(slot).filter(Boolean);
    slots.forEach((el) => el.classList.add('opacity-50'));

    if (!cache.has(key)) {
      try {
        cache.set(key, await fetchSectionDocument(item.url, sectionId));
      } catch (error) {
        // Formulier staat al goed; bij een fout tonen we de echte productpagina.
        if (token === requestToken) window.location.href = item.url;
        return;
      }
    }
    if (token !== requestToken) return;

    const doc = cache.get(key);
    slots.forEach((el) => {
      // Kopie, zodat het Document in de cache compleet blijft voor een volgende keer.
      const fresh = doc.querySelector(`[data-configurator-part="${el.dataset.configuratorSlot}"]`);
      if (fresh) el.replaceChildren(fresh.cloneNode(true));
      el.classList.remove('opacity-50');
    });
    reinitWidgets();
    updateStickyPrice();
  }

  /** Initialiseert galerij en accordeon opnieuw na het wisselen van de slots. */
  function reinitWidgets() {
    const gallery = root.querySelector('[data-configurator-slot="gallery"] [data-custom-slider]');
    if (gallery && window.GallerySliderPlugin) new window.GallerySliderPlugin(gallery);
    const after = slot('after');
    if (after) initAccordeon(after);
  }
}

/** Klemt een waarde binnen min/max van een input. */
function clampToInput(input, value) {
  return Math.min(Number(input.max), Math.max(Number(input.min), value));
}

/**
 * Doelgroep die uit een formaat volgt als de bezoeker die nog niet koos: 90x120/100x150 zijn
 * kindformaten, 240x220 is voor volwassenen; 140x200 is allebei (null).
 * @returns {'adult'|'child'|null}
 */
function inferAudience(size) {
  if (!size) return null;
  const only = Object.keys(AUDIENCE_SIZES).filter((key) => AUDIENCE_SIZES[key].includes(size));
  return only.length === 1 ? only[0] : null;
}

/**
 * Geeft de adviesrijen van de doelgroep: de kind-tabel bij 'child', anders de volwassen tabel
 * (ook zolang er nog geen doelgroep is gekozen).
 * @returns {Array<{size: string, weight: string, min: number, max: number, minLength?: number, maxLength?: number}>}
 */
function adviceRows(advice, audience) {
  return audience === 'child' ? advice.kid : advice.adult;
}

/** Past de lichaamslengte binnen de (optionele) minLength/maxLength van een adviesrij? */
function fitsLength(row, length) {
  return (!row.minLength || length >= row.minLength) && (!row.maxLength || length <= row.maxLength);
}

/** Aantal kg dat het lichaamsgewicht buiten het bereik van een rij valt (0 = binnen bereik). */
function distance(row, weight) {
  return weight < row.min ? row.min - weight : weight > row.max ? weight - row.max : 0;
}

/** Afstand tot het midden van het bereik; breekt gelijkspel tussen rijen binnen bereik. */
function centerDistance(row, weight) {
  return Math.abs(weight - (row.min + row.max) / 2);
}

/**
 * Aanbevolen formaten. Zonder doelgroep geen advies. Volwassene: 140x200 én 240x220 (met gewicht:
 * elk formaat met een deken die even goed past als de beste). Kind: zonder lengte geen advies, met
 * lengte elk kindformaat dat qua lengte past (tot 110 cm 90x120, tot 135 cm 100x150, vanaf 120 cm
 * 140x200), met gewicht erbij weer alleen de formaten die even goed passen als de beste. Passen er
 * meerdere, dan worden die gelijkwaardig aanbevolen en kiest de bezoeker zelf.
 * @returns {string[]} size_handles in SIZE_ORDER, bv. ['140x200cm', '240x220cm']; leeg = geen advies
 */
function recommendedSizes(advice, profile, audience) {
  if (!audience || (audience === 'child' && profile.length === null)) return [];
  let rows = rankAll(advice, profile, audience);
  if (profile.weight !== null && rows.length) {
    const bestDistance = distance(rows[0], profile.weight);
    rows = rows.filter((row) => distance(row, profile.weight) === bestDistance);
  }
  const sizes = rows.map((row) => row.size);
  return SIZE_ORDER.filter((size) => sizes.includes(size));
}

/**
 * Rangschikt alle dekens van de doelgroep die qua lengte passen (kind; volwassen rijen hebben geen
 * lengtegrens): eerst binnen gewichtsbereik (zonder gewicht: tabelvolgorde), dan het kleinste
 * passende formaat, dan dichtst bij het midden. Element 0 is het advies.
 * @returns {Array} adviesrijen, beste eerst
 */
function rankAll(advice, profile, audience) {
  const rows = adviceRows(advice, audience).filter(
    (row) => audience !== 'child' || profile.length === null || fitsLength(row, profile.length)
  );
  if (profile.weight === null) return rows;
  return rows.sort(
    (a, b) =>
      distance(a, profile.weight) - distance(b, profile.weight) ||
      SIZE_ORDER.indexOf(a.size) - SIZE_ORDER.indexOf(b.size) ||
      centerDistance(a, profile.weight) - centerDistance(b, profile.weight)
  );
}

/**
 * Rangschikt de gewichten binnen één formaat voor de 1e/2e/3e-keus-labels. Er is altijd een advies:
 * eerst de rijen van de eigen tabel binnen 10 kg van hun bereik, anders alle rijen van dit formaat
 * binnen 10 kg (een kind van 163 cm / 83 kg op 140x200 krijgt zo de volwassen gewichten, die dekens
 * staan toch in de lijst), anders de dichtstbijzijnde rijen. Bij 240x220 met partnergewicht gelden
 * de "double"-rijen (tweepersoonstabel) op het gemiddelde gewicht van beide personen.
 * @returns {Array} maximaal 3 adviesrijen, beste eerst
 */
function rankWithinSize(advice, profile, audience, size) {
  const all = [...advice.kid, ...advice.adult].filter((row) => row.size === size);
  let rows;
  let weight = profile.weight;
  if (size === DOUBLE_SIZE && profile.partner !== null && advice.double) {
    rows = advice.double;
    weight = (profile.weight + profile.partner) / 2;
  } else {
    rows = adviceRows(advice, audience).filter((row) => row.size === size);
  }
  const within = (list) => list.filter((row) => distance(row, weight) <= 10);
  let candidates = within(rows);
  if (!candidates.length) candidates = within(all);
  if (!candidates.length) candidates = all;
  // candidates is altijd een nieuwe array (filter), dus sorteren raakt de adviestabel niet.
  return candidates
    .sort((a, b) => distance(a, weight) - distance(b, weight) || centerDistance(a, weight) - centerDistance(b, weight))
    .slice(0, 3);
}

/**
 * Bepaalt de beginkeuze: geldige URL-params (?size=&weight=&cover=, of de oude Nederlandse namen)
 * gaan voor; anders het product dat de server rendert (alleen bij ?view=configurator op een echt
 * product); anders leeg.
 * @returns {{size: ?string, weight: ?string, cover: ?string}}
 */
function initialState(items, defaultId, params) {
  const read = (key) => PARAMS[key].map((name) => params.get(name)).find(Boolean) || null;
  const requested = { size: withUnit(read('size'), 'cm'), weight: withUnit(read('weight'), 'kg'), cover: read('cover') };
  if (requested.size && items.some((i) => i.size === requested.size)) {
    return normalize(items, requested);
  }
  const fallback = defaultId && items.find((i) => i.id === defaultId);
  if (fallback) return { size: fallback.size, weight: fallback.weight, cover: fallback.coverKey };
  return { size: null, weight: null, cover: null };
}

/** URL-waarde naar interne vorm: '140x200' en '140x200cm' worden allebei '140x200cm'. */
function withUnit(value, unit) {
  if (!value) return null;
  return value.endsWith(unit) ? value : `${value}${unit}`;
}

/** Interne vorm naar URL-waarde: '6kg' wordt '6'. */
function withoutUnit(value, unit) {
  return value ? value.replace(new RegExp(`${unit}$`), '') : null;
}

/**
 * Zoekt het echte product bij een keuze.
 * @returns {object|undefined} item uit #configurator-data
 */
function resolve(items, { size, weight, cover }) {
  return items.find((i) => i.size === size && i.weight === weight && i.coverKey === cover);
}

/**
 * Maakt een keuze geldig na een wijziging hoger in de stappen: een gewicht dat niet bij het
 * formaat hoort vervalt (de bezoeker kiest opnieuw); zodra er een gewicht is, wordt een
 * ontbrekende of niet-bestaande hoes 'zonder hoes'.
 * @returns {object} dezelfde (aangepaste) state
 */
function normalize(items, state) {
  if (!state.size || !items.some((i) => i.size === state.size)) {
    state.size = null;
    state.weight = null;
    state.cover = null;
    return state;
  }
  if (!items.some((i) => i.size === state.size && i.weight === state.weight)) state.weight = null;
  if (!state.weight) {
    state.cover = null;
    return state;
  }
  if (!state.cover || !resolve(items, state)) state.cover = NO_COVER;
  return state;
}

/**
 * Vult de optielijst van een stap met kopieën van de <template data-option-template>.
 * De actieve optie krijgt de klassen uit [data-active-class] en een uitverkochte die uit
 * [data-soldout-class] (zelfde idee als data-active-thumbnail-classes in src/slider.js), zodat de
 * markup gelijk blijft aan de server-gerenderde swatches.
 * @param {HTMLElement} step - [data-step]-blok
 * @param {Array} values - opties
 * @param {Function} fill - (button, value) => boolean; vult de knop en geeft terug of hij actief is
 */
function renderOptions(step, values, fill) {
  const template = step.querySelector('[data-option-template]');
  const spacer = step.querySelector('[data-spacer-template]');
  const nodes = values.map((value) => {
    const node = template.content.firstElementChild.cloneNode(true);
    const button = node.matches('[data-option]') ? node : node.querySelector('[data-option]');
    if (typeof value === 'string') button.dataset.option = value;
    const active = fill(button, value);
    button.setAttribute('aria-pressed', String(active));
    if (button.dataset.soldOut === 'true') addDataClasses(node, 'soldoutClass');
    if (active) addDataClasses(node, 'activeClass');
    return node;
  });
  if (spacer) nodes.push(spacer.content.firstElementChild.cloneNode(true));
  step.querySelector('[data-step-options]').replaceChildren(...nodes);
}

/** Zet op een node en zijn nakomelingen de klassen uit hun eigen data-attribuut (bv. data-active-class). */
function addDataClasses(node, datasetKey) {
  const attribute = `data-${datasetKey.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
  [node, ...node.querySelectorAll(`[${attribute}]`)]
    .filter((el) => el.dataset[datasetKey])
    .forEach((el) => el.classList.add(...el.dataset[datasetKey].split(' ')));
}

/**
 * Zet de badge boven een optie. rank 1 (1e keus / aanbevolen) krijgt via data-rank een eigen,
 * opvallender stijl (zie de [data-badge]-klassen in de section).
 */
function setBadge(button, text, rank) {
  const badge = button.querySelector('[data-badge]');
  badge.textContent = text;
  badge.hidden = !text;
  badge.dataset.rank = text ? String(rank) : '';
}

function uniqueBy(list, key) {
  const seen = new Set();
  return list.filter((item) => !seen.has(item[key]) && seen.add(item[key]));
}
